import {memo, useRef, useState} from "react";
import {withDialog} from "../../../../../../../../state/withDialog";
import {DialogHeader, DialogTitle, DialogBody, DialogFooter} from "../../../../../../../../components/ui/dialog";
import {Box, HStack, Stack} from "@chakra-ui/react";
import {Button} from "../../../../../../../../components/ui/button";
import {ArrowDownTrayIcon} from "@heroicons/react/16/solid";
import {DiagramExportRenderer} from "../DiagramExportRenderer";
import {usePromiseState} from "../../../../../../../../hooks/promiseState";
import {exportNodeToPng} from "../../../../../../../../utils/exportPng";
import {ClientDiagram} from "../../utility/types";
import {Checkbox} from "../../../../../../../../components/ui/checkbox";
import {Alert} from "../../../../../../../../components/ui/alert";
import type {SwimlaneConfig} from "../../index";

export const ExportDialog = withDialog(
  memo(
    ({
      onClose,
      diagram,
      showSwimlanes = false,
      swimlanes,
    }: {
      onClose: () => void;
      diagram: ClientDiagram;
      showSwimlanes?: boolean;
      swimlanes?: SwimlaneConfig[];
    }) => {
      const [showBackgroundGrid, setShowBackgroundGrid] = useState(true);
      const [transparentBackground, setTransparentBackground] = useState(false);

      const offscreenRef = useRef<HTMLDivElement>(null);
      const filename = "export.png";

      const [exporting, doExport] = usePromiseState(async () => {
        const dataUrl = await exportNodeToPng(offscreenRef.current!, {
          backgroundColor: transparentBackground ? undefined : "#f6faff",
        });

        const link = document.createElement("a");
        link.download = filename;
        link.href = dataUrl;
        link.click();

        onClose();
      }, [onClose, transparentBackground]);

      return (
        <>
          <DialogHeader>
            <DialogTitle>Export data flow</DialogTitle>
          </DialogHeader>
          <DialogBody fontSize="sm">
            <Stack gap={6}>
              <Stack>
                <Checkbox
                  size="sm"
                  colorPalette="blue"
                  checked={showBackgroundGrid}
                  onCheckedChange={d => setShowBackgroundGrid(d.checked === true)}
                >
                  Show background grid
                </Checkbox>
                <Checkbox
                  size="sm"
                  colorPalette="blue"
                  checked={transparentBackground}
                  onCheckedChange={d => setTransparentBackground(d.checked === true)}
                >
                  Transparent background
                </Checkbox>
              </Stack>
              {exporting.lastError != null && (
                <Alert status="error" title="An error occurred">
                  {`${exporting.lastError}`}
                </Alert>
              )}
            </Stack>
          </DialogBody>
          <DialogFooter>
            <HStack gap="3">
              <Button variant="outline" onClick={onClose} disabled={exporting.inProgress}>
                Cancel
              </Button>
              <Button colorPalette="blue" onClick={doExport} loading={exporting.inProgress}>
                <ArrowDownTrayIcon />
                Download .png
              </Button>
            </HStack>
          </DialogFooter>
          <Box position="absolute" pointerEvents="none" top="1000000px" left="1000000px">
            <DiagramExportRenderer
              ref={offscreenRef}
              diagram={diagram}
              showBackgroundGrid={showBackgroundGrid}
              showSwimlanes={showSwimlanes}
              swimlanes={swimlanes}
            />
          </Box>
        </>
      );
    },
  ),
  {lazyMount: true, unmountOnExit: true, placement: "center", scrollBehavior: "inside"},
);
